// @ts-strict-ignore
import Meta from './Meta'
import * as S from './elements'
import { Box, Container, Icon } from '@gassan-ui'
import { useTranslation } from 'next-i18next'
import { FC, Fragment } from 'react'
import { MarginBottomProps } from 'styled-system'

//https://gist.github.com/kottenator/9d936eb3e4e3c3e02598#gistcomment-3003402
const getRange = (start: number, end: number) => {
  return Array(end - start + 1)
    .fill(0)
    .map((v, i) => i + start)
}

const pagination = (currentPage: number, pageCount: number) => {
  let delta: number
  if (pageCount <= 7) {
    // delta === 7: [1 2 3 4 5 6 7]
    delta = 7
  } else {
    // delta === 2: [1 ... 4 5 6 ... 10]
    // delta === 4: [1 2 3 4 ... 10]
    delta = currentPage > 4 && currentPage < pageCount - 3 ? 2 : 4
  }

  const range = {
    start: Math.round(currentPage - delta / 2),
    end: Math.round(currentPage + delta / 2),
  }

  if (range.start - 1 === 1 || range.end + 1 === pageCount) {
    range.start += 1
    range.end += 1
  }

  let pages: any =
    currentPage > delta
      ? getRange(Math.min(range.start, pageCount - delta), Math.min(range.end, pageCount))
      : getRange(1, Math.min(pageCount, delta + 1))

  const withDots = (value, pair) => (pages.length + 1 !== pageCount ? pair : [value])

  if (pages[0] !== 1) {
    pages = withDots(1, [1, '...']).concat(pages)
  }

  if (pages[pages.length - 1] < pageCount) {
    pages = pages.concat(withDots(pageCount, ['...', pageCount]))
  }

  return pages
}

type Props = MarginBottomProps & {
  limit: number
  currentPage: number
  totalCount: number
  setPage: (page: number) => void
}

const Pagination: FC<Props> = ({ limit, currentPage, totalCount, setPage, mb = '1rem' }) => {
  const { t } = useTranslation('global')
  const totalPages = Math.ceil(totalCount / limit)
  const canGoBack = currentPage > 1
  const canGoForward = currentPage < totalPages
  const pages =
    totalPages <= 5
      ? Array.from(new Array(totalPages)).map((_, i) => i + 1)
      : pagination(currentPage, totalPages)

  const handleGoToPage = (page: number) => () => {
    const containerEl = document.getElementById('item-container')
    setPage(page)
    if (containerEl && containerEl.offsetTop) {
      window.scrollTo({ top: containerEl.offsetTop - 80, left: 0, behavior: 'smooth' })
    }
  }
  if (totalPages === 1) {
    return null
  }

  return (
    <>
      <Meta totalPages={totalPages} currentPage={currentPage} />
      <Container
        width="100%"
        display="flex"
        justifyContent="center"
        mt={['3rem', '6.5rem']}
        mb={mb}
        data-test-id="pagination"
      >
        <S.Arrow onClick={handleGoToPage(currentPage - 1)} disabled={!canGoBack}>
          <Icon icon="arrow-left" />
        </S.Arrow>
        <Box display={['none', 'flex']}>
          {pages.map((page, index) =>
            page === '...' ? (
              <Fragment key={index}>
                <S.Dots>...</S.Dots>
              </Fragment>
            ) : (
              <S.Button key={index} isActive={page === currentPage} onClick={handleGoToPage(page)}>
                {page}
              </S.Button>
            ),
          )}
        </Box>
        <Box display={['flex', 'none']} color="shades.500" mx="0.5rem">
          <S.Button as="span" color="shades.800">
            {currentPage}
          </S.Button>
          <S.Button as="span">{t('ofPagination')}</S.Button>
          <S.Button as="span">{totalPages}</S.Button>
        </Box>

        <S.Arrow onClick={handleGoToPage(currentPage + 1)} disabled={!canGoForward}>
          <Icon icon="arrow-right" />
        </S.Arrow>
      </Container>
    </>
  )
}

export default Pagination
